// A job's period (finance-real, the user 2026-10-22): how it bills and its days. How it bills is one dropdown (DD-061: a
// choice between kinds is one dropdown, never toggle buttons; OptionDropdown, DD-056): Recurring, One-off or Over a number
// of posts. Recurring and One-off pick a start and an end in one date-range picker (DateRangePicker) holding the Monthly
// shortcut, which sets the end to the day before the same date next month (1 Oct-31 Oct, 17 Oct-16 Nov); any end can be
// picked by hand. Recurring repeats: the next job's period is as long, from the day after this one ends. A one-off's days
// are the work's, one day or several, or none yet. Over a number of posts counts its posts from a start day, with no end.
// A job not started has no days yet (setting it active gives it a month from the day it starts), so it
// shows the dropdown alone. The job form and the job drawer's in-place editing share it.
import type { ReactNode } from "react";
import { DateRangePicker, type RangeShortcut } from "@/components/ui/DateRangePicker";
import { inputClassName } from "@/components/ui/FormField";
import { OptionDropdown, type DropdownOption } from "@/components/ui/OptionDropdown";
import { ENTITY_CONFIG } from "@/core/entityConfig";
import { Required } from "../components/Required";
import type { JobRhythmInput, LineJob } from "../types";
import { dateLabel } from "../utils/format";
import { NO_PERIOD, periodText } from "../utils/names";

const postsWord = ENTITY_CONFIG.post.plural.toLowerCase();

export type PeriodMode = JobRhythmInput["mode"];

export interface PeriodValue {
  mode: PeriodMode;
  /** The first day, "" when none is set. */
  startsOn: string;
  /** The last day, "" when none is set: a one-off on one day, a run of posts, or an end not picked yet. */
  endsOn: string;
  /** The posts a run counts, as typed. */
  every: string;
}

const MODES: DropdownOption<PeriodMode>[] = [
  { value: "recurring", label: "Recurring" },
  { value: "one_off", label: "One-off" },
  { value: "posts", label: `Over a number of ${postsWord}` },
];

const iso = (y: number, m: number, d: number): string => `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

/** The Monthly shortcut: the day before the same date next month, or that month's last when it is shorter. */
export function monthFrom(startsOn: string): string {
  const [y, m, d] = startsOn.split("-").map(Number);
  const [ny, nm] = m === 12 ? [y + 1, 1] : [y, m + 1];
  const day = new Date(Date.UTC(ny, nm - 1, Math.min(d, new Date(Date.UTC(ny, nm, 0)).getUTCDate())));
  day.setUTCDate(day.getUTCDate() - 1);
  return iso(day.getUTCFullYear(), day.getUTCMonth() + 1, day.getUTCDate());
}

const SHORTCUTS: RangeShortcut[] = [{ label: "Monthly", endFrom: monthFrom }];

export const periodOf = (job: Pick<LineJob, "rhythm" | "startsOn" | "endsOn">): PeriodValue => ({
  mode: job.rhythm.mode,
  startsOn: job.startsOn ?? "",
  endsOn: job.endsOn ?? "",
  every: job.rhythm.mode === "posts" ? String(job.rhythm.every) : "8",
});

/** A new job's period: recurring, a month from `today`. */
export const newPeriod = (today: string): PeriodValue => ({ mode: "recurring", startsOn: today, endsOn: monthFrom(today), every: "8" });

/** The rhythm the picker says, or null while its number of posts is out of range. */
export function rhythmFrom(value: PeriodValue): JobRhythmInput | null {
  if (value.mode !== "posts") return { mode: value.mode };
  const n = Number(value.every);
  return Number.isInteger(n) && n >= 1 && n <= 60 ? { mode: "posts", every: n } : null;
}

/** What keeps the period from being sent: its posts out of range, a start or (recurring) an end missing. */
export function periodError(value: PeriodValue, dated: boolean): string | undefined {
  if (rhythmFrom(value) === null) return `From 1 to 60 ${postsWord}`;
  if (!dated || value.mode === "one_off") return undefined;
  if (!value.startsOn) return "The day its period starts";
  if (value.mode === "recurring" && !value.endsOn) return "The day it ends, or Monthly";
  return undefined;
}

/** The days the server takes: a run of posts has no end, a job not started no days. */
export const periodDays = (value: PeriodValue, dated: boolean): { startsOn: string | null; endsOn: string | null } =>
  dated ? { startsOn: value.startsOn || null, endsOn: value.mode === "posts" ? null : value.endsOn || null } : { startsOn: null, endsOn: null };

/** Switching kind keeps the days that still make sense: a run of posts has no end, recurring needs one. */
function switchMode(value: PeriodValue, mode: PeriodMode): PeriodValue {
  if (mode === "posts") return { ...value, mode, endsOn: "" };
  if (mode === "recurring" && value.startsOn && !value.endsOn) return { ...value, mode, endsOn: monthFrom(value.startsOn) };
  return { ...value, mode };
}

/** What the picked period comes to, in words. */
function meaning(value: PeriodValue, dated: boolean): string {
  const days = dated && value.startsOn ? periodText(value.startsOn, value.endsOn || null) : null;
  switch (value.mode) {
    case "recurring":
      if (!dated) return "It repeats: a month from the day it starts, then the same length again.";
      if (!days || !value.endsOn) return "It repeats, each period as long as this one.";
      return value.endsOn === monthFrom(value.startsOn) ? `${days}, then a month at a time.` : `${days}, then periods as long, one after another.`;
    case "posts":
      return `A run of ${value.every || "N"} ${postsWord}${dated && value.startsOn ? ` from ${dateLabel(value.startsOn)}` : ""}; the next run follows once they are out.`;
    case "one_off":
      return days ? `Once, ${days}.` : dated ? "Once, no day set." : "Once.";
  }
}

/** A field's label over its control, as FormField has it, in a div: a label would forward a click inside the calendar's
 * popover to its trigger and close it. */
function Field({ label, error, children }: { label: ReactNode; error?: string; children: ReactNode }) {
  return (
    <div>
      <span className="block text-xs font-medium text-gray-600 mb-1">{label}</span>
      {children}
      {error && <span className="block text-xs text-red-600 mt-1">{error}</span>}
    </div>
  );
}

interface Props {
  value: PeriodValue;
  onChange: (value: PeriodValue) => void;
  /** Show the days: false for a job not started. */
  dated: boolean;
  /** Under the days, or under how it bills for a job not started. */
  error?: string;
  disabled?: boolean;
}

export function PeriodPicker({ value, onChange, dated, error, disabled = false }: Props) {
  const posts = value.mode === "posts";
  return (
    <div className="space-y-3">
      <div className={posts ? "grid grid-cols-[1fr_6rem] gap-3" : undefined}>
        <Field label="How it bills" error={dated ? undefined : error}>
          <OptionDropdown variant="input" ariaLabel="How it bills" value={value.mode} options={MODES} disabled={disabled} onChange={(mode) => onChange(switchMode(value, mode))} />
        </Field>
        {posts && (
          <Field label={ENTITY_CONFIG.post.plural}>
            <input
              type="number"
              min={1}
              max={60}
              aria-label={`${postsWord} a run counts`}
              className={inputClassName}
              value={value.every}
              disabled={disabled}
              onChange={(e) => onChange({ ...value, every: e.target.value })}
            />
          </Field>
        )}
      </div>
      {/* DD-056: a start (and a recurring job's end) is required once it is dated; a one-off's days are not. */}
      {dated && (
        <Field label={value.mode === "one_off" ? "Days" : <Required>{posts ? "Starts on" : "Period"}</Required>} error={error}>
          <DateRangePicker
            ariaLabel={posts ? "Starts on" : "Period"}
            single={posts}
            shortcuts={SHORTCUTS}
            clearable={value.mode === "one_off"}
            placeholder={value.mode === "one_off" ? NO_PERIOD : "Pick the start"}
            value={{ from: value.startsOn, to: posts ? "" : value.endsOn }}
            disabled={disabled}
            onChange={(range) => onChange({ ...value, startsOn: range.from, endsOn: range.to })}
          />
        </Field>
      )}
      <p className="text-xs text-gray-500">{meaning(value, dated)}</p>
    </div>
  );
}
